import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  getRedirectResult,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  initializeAuth,
  inMemoryPersistence,
  setPersistence,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";

import config from "@/config";
import { getSuarteEnvironment } from "@/utils/environment.utils";

const app = initializeApp(config.firebase);

const rawEnv =
  typeof window !== "undefined" ? getSuarteEnvironment() : { isNativeWrapper: false };
const userAgent =
  typeof window !== "undefined" && typeof window.navigator?.userAgent === "string"
    ? window.navigator.userAgent
    : "";
const uaLooksNative = userAgent.includes("SuarteApp-iOS");
const isNativeWrapper = Boolean(rawEnv.isNativeWrapper || uaLooksNative);

const persistenceLayers = isNativeWrapper
  ? [browserSessionPersistence, indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence]
  : [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence, inMemoryPersistence];

const auth = initializeAuth(app, {
  persistence: persistenceLayers,
  popupRedirectResolver: browserPopupRedirectResolver,
});

auth.useDeviceLanguage();

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

const ensureRedirectPersistence = (() => {
  let promise = null;
  return () => {
    if (!promise) {
      promise = setPersistence(auth, browserSessionPersistence).catch((error) => {
        console.error("[firebase] failed to set redirect persistence", {
          code: error?.code,
          message: error?.message,
        });
        throw error;
      });
    }
    return promise;
  };
})();

const POPUP_FALLBACK_ERRORS = new Set([
  "auth/operation-not-supported-in-this-environment",
  "auth/popup-blocked",
]);

export const signInWithGoogle = async () => {
  try {
    const res = await signInWithPopup(auth, googleProvider);
    return res;
  } catch (err) {
    console.error("[firebase] signInWithGoogle error", {
      code: err?.code,
      message: err?.message,
      full: err,
    });

    // Storage blocked → use in-memory and retry popup
    if (err.code === "auth/web-storage-unsupported") {
      try {
        await setPersistence(auth, inMemoryPersistence);
        return await signInWithPopup(auth, googleProvider);
      } catch (fallbackError) {
        console.error("[firebase] popup failed after in-memory", fallbackError);
        return fallbackError;
      }
    }

    if (POPUP_FALLBACK_ERRORS.has(err.code)) {
      await ensureRedirectPersistence();
      await signInWithRedirect(auth, googleProvider);
      return {
        code: "auth/redirect-in-progress",
        reason: err.code,
      };
    }

    return err;
  }
};

export const resolveGoogleRedirectResult = async () => {
  try {
    return await getRedirectResult(auth);
  } catch (error) {
    console.error("[firebase] resolveGoogleRedirectResult error", {
      code: error?.code,
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
    });
    throw error;
  }
};

export function loginWithCustomToken(token) {
  return signInWithCustomToken(auth, token);
}

export const logoutUser = async () => {
  // A push subscription is device-level. Remove it before signing out so a
  // shared browser cannot continue receiving the previous user's alerts.
  try {
    const { disablePushNotifications } = await import('./pushNotifications.service');
    await disablePushNotifications();
  } catch (_error) {
    // Signing out must remain possible when Push is unsupported or offline.
  }
  return signOut(auth);
};

export { auth };
